import React, { useEffect, useState } from "react";
import { GoogleMap, LoadScript, DirectionsRenderer } from "@react-google-maps/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BACKEND_URL = process.env.REACT_APP_BASE_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function TransporterDashboard() {
  const [requests, setRequests] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [directions, setDirections] = useState(null);

  const [rejectComment, setRejectComment] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const loadRequests = async () => {
    const token = sessionStorage.getItem("token");
    const res = await fetch(`${BACKEND_URL}/transporter/trip-requests`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    setRequests(data.requests || []);
  };

  const loadDrivers = async () => {
    const token = sessionStorage.getItem("token");
    const res = await fetch(`${BACKEND_URL}/transporter/drivers`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    setDrivers(data);
  };

  useEffect(() => {
    loadRequests();
    loadDrivers();
  }, []);

  const openRouteMap = (req) => {
    setSelectedRequest(req);

    const stops = req.route.stops;
    if (!stops || stops.length < 2) {
      setDirections(null);
      return;
    }

    const origin = stops[0].address;
    const destination = stops[stops.length - 1].address;
    const waypoints = stops.slice(1, -1).map((s) => ({
      location: s.address,
      stopover: true,
    }));

    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          setDirections(null);
        }
      }
    );
  };

  /* ========= ACCEPT REQUEST ========= */
  const acceptRequest = async (id) => {
    const token = sessionStorage.getItem("token");
    const res = await fetch(
      `${BACKEND_URL}/transporter/trip-requests/${id}/accept`,
      {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (res.ok) {
      toast.success("Request Accepted!");
      loadRequests();
    }
  };

  /* ========= REJECT REQUEST ========= */
  const rejectRequest = async () => {
    if (!rejectComment.trim()) {
      toast.error("Reject comment required");
      return;
    }

    const token = sessionStorage.getItem("token");
    const res = await fetch(
      `${BACKEND_URL}/transporter/trip-requests/${selectedRequest.request_id}/reject`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: rejectComment }),
      }
    );

    if (res.ok) {
      toast.success("Request Rejected");
      setIsRejectModalOpen(false);
      loadRequests();
    }
  };

  /* ========= ASSIGN DRIVER ========= */
  const assignDriver = async () => {
    if (!selectedDriver) {
      toast.error("Select a driver");
      return;
    }

    const token = sessionStorage.getItem("token");
    const res = await fetch(
      `${BACKEND_URL}/transporter/trip-requests/${selectedRequest.request_id}/assign-driver`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ driver_id: selectedDriver }),
      }
    );

    if (res.ok) {
      toast.success("Driver Assigned!");
      setIsAssignModalOpen(false);
      loadRequests();
    }
  };

  return (
    <div className="min-h-screen bg-[#1f1f1f] text-white flex">
      {/* Left Sidebar */}
      <div className="w-1/3 bg-black p-6 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-orange-500">
            Transporter Dashboard
          </h1>
          <button
            className="text-orange-400"
            onClick={() => {
              sessionStorage.removeItem("token");
              window.location.href = "/";
            }}
          >
            Logout
          </button>
        </div>

        <h2 className="text-lg text-orange-400 mb-4 font-semibold">
          Trip Requests
        </h2>

        {requests.length === 0 && (
          <p className="text-gray-400">No requests found</p>
        )}

        {requests.map((req) => (
          <div
            key={req.request_id}
            className="bg-[#2a2a2a] p-4 rounded-lg border border-gray-700 mb-3"
          >
            <p className="text-orange-400 font-semibold">
              Request #{req.request_id}
            </p>
            <p className="text-sm text-gray-300">
              Status:{" "}
              <span
                className={`${req.status === "pending"
                  ? "text-yellow-400"
                  : req.status === "accepted"
                    ? "text-green-400"
                    : req.status === "driver_assigned"
                      ? "text-blue-400"
                      : "text-red-400"
                  }`}
              >
                {req.status}
              </span>
            </p>

            <p className="text-sm text-gray-300">
              Departure:{" "}
              <span className="text-orange-400">
                {req.route.departure_date && req.route.departure_time
                  ? new Date(`${req.route.departure_date}T${req.route.departure_time}`).toLocaleString()
                  : "Not set"}
              </span>
            </p>

            <button
              onClick={() => openRouteMap(req)}
              className="text-orange-400 hover:underline mt-2 text-sm"
            >
              View Route
            </button>

            {req.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => acceptRequest(req.request_id)}
                  className="px-3 py-1 bg-green-600 rounded text-sm"
                >
                  Accept
                </button>
                <button
                  onClick={() => {
                    setSelectedRequest(req);
                    setIsRejectModalOpen(true);
                  }}
                  className="px-3 py-1 bg-red-600 rounded text-sm"
                >
                  Reject
                </button>
              </div>
            )}

            {req.status === "accepted" && (
              <button
                className="mt-3 px-3 py-1 bg-blue-600 rounded text-sm"
                onClick={() => {
                  setSelectedRequest(req);
                  setIsAssignModalOpen(true);
                }}
              >
                Assign Driver
              </button>
            )}

            {req.driver_assignment && (
              <p className="text-sm mt-2 text-green-400">
                Driver Assigned: {
                  drivers.find((d) => d.id === req.driver_assignment.driver_id)?.username ||
                  req.driver_assignment.driver_id
                }
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Map Panel */}
      <div className="w-2/3 h-screen">
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: 22.5937, lng: 81.9629 }}
            zoom={5}
          >
            {directions && <DirectionsRenderer directions={directions} />}
          </GoogleMap>
        </LoadScript>
      </div>

      <ToastContainer theme="dark" />

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center">
          <div className="bg-[#2a2a2a] p-6 rounded-lg w-96 border border-gray-600">
            <h3 className="text-orange-400 text-lg font-semibold">
              Reject Request #{selectedRequest?.request_id}
            </h3>

            <textarea
              className="w-full mt-3 p-2 bg-black border border-gray-700 text-white rounded"
              placeholder="Enter rejection reason"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={4}
            />

            <div className="flex justify-end gap-2 mt-3">
              <button
                className="px-3 py-1 bg-gray-600 rounded"
                onClick={() => setIsRejectModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-red-600 rounded"
                onClick={rejectRequest}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center">
          <div className="bg-[#2a2a2a] p-6 rounded-lg w-96 border border-gray-600">
            <h3 className="text-orange-400 text-lg font-semibold">
              Assign Driver
            </h3>

            <select
              className="w-full p-2 bg-black border border-gray-700 mt-3 rounded"
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
            >
              <option value="">-- Select Driver --</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.username}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2 mt-3">
              <button
                className="px-3 py-1 bg-gray-600 rounded"
                onClick={() => setIsAssignModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-blue-600 rounded"
                onClick={assignDriver}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransporterDashboard;
